import type { FieldMappings } from '../../../../integrations/relativity/relativity-field-mapping.entity.js';
import type { DocChronologyDoc } from './doc-chronology-doc.entity.js';

export const DocChronologyDownloadColumn = {
  DocumentTitle: 'Document Title',
  DocumentName: 'Document Name',
  DocumentDateVerbatim: 'Document Date (verbatim)',
  DocumentDateStandardised: 'Document Date (standardised)',
  DocumentType: 'Document Type',
  Summary: 'Summary',
  Facts: 'Facts',
  Id: 'Id',
} as const;

export type DocChronologyDownloadColumn =
  (typeof DocChronologyDownloadColumn)[keyof typeof DocChronologyDownloadColumn];

export const DocChronologyDownloadColumnOptions = Object.values(DocChronologyDownloadColumn);

// Excel cell character limit is 32,767
// use 10,000 as a (very) safe limit (plus anything anywhere near this length would be largely unreadable anyway)
const CELL_LENGTH_LIMIT = 10_000;

/**
 * Prepares a CSV row for a document chronology document.
 * Each doc is a row, with configurable columns.
 */
export function prepareDocChronologyCSVRow(
  chronDoc: DocChronologyDoc,
  columns: DocChronologyDownloadColumn[],
  relativityColumns: string[] = [],
): string[] {
  // Build facts string from attached facts
  const factsText = chronDoc.facts
    .map((fsr) => fsr.fact.description)
    .filter(Boolean)
    .join('\n\n');

  const truncatedFacts =
    factsText && factsText.length > CELL_LENGTH_LIMIT
      ? `${factsText.slice(0, CELL_LENGTH_LIMIT)}...`
      : factsText;

  const summary = chronDoc.summary ?? chronDoc.doc.summary;
  const truncatedSummary =
    summary !== null && summary.length > CELL_LENGTH_LIMIT
      ? `${summary.slice(0, CELL_LENGTH_LIMIT)}...`
      : summary;

  const columnMapping: Record<DocChronologyDownloadColumn, string | Date | null | undefined> = {
    [DocChronologyDownloadColumn.DocumentTitle]: chronDoc.title ?? chronDoc.doc.title,
    [DocChronologyDownloadColumn.DocumentName]: chronDoc.doc.file.name,
    [DocChronologyDownloadColumn.DocumentDateVerbatim]: chronDoc.dateText ?? chronDoc.doc.dateText,
    [DocChronologyDownloadColumn.DocumentDateStandardised]: chronDoc.date ?? chronDoc.doc.date,
    [DocChronologyDownloadColumn.DocumentType]: chronDoc.doc.type,
    [DocChronologyDownloadColumn.Summary]: truncatedSummary,
    [DocChronologyDownloadColumn.Facts]: truncatedFacts,
    [DocChronologyDownloadColumn.Id]: chronDoc.id,
  };

  const standardValues = columns.map((column) => columnMapping[column]?.toString() ?? '');

  // Extract relativity field values from file metadata
  const relativityValues = relativityColumns.map((fieldKey) => {
    const value = chronDoc.doc.file.fileMetadata?.relativityFields[fieldKey];
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join('; ');
    return value.toString();
  });

  return [...standardValues, ...relativityValues];
}

/**
 * Extracts unique document IDs from doc chronology documents.
 * Returns a sorted array of unique document IDs.
 */
export function extractUniqueDocumentsFromDocChronology(chronDocs: DocChronologyDoc[]): string[] {
  const uniqueDocIds = new Set<string>();

  for (const chronDoc of chronDocs) {
    if (chronDoc.docId) {
      uniqueDocIds.add(chronDoc.docId);
    }
  }

  // Return sorted array for consistent hashing
  return Array.from(uniqueDocIds).sort();
}

/**
 * Prepares the LLM input for formatting a doc chronology document for word download.
 */
export function prepareDocChronologyLLMInput(chronDoc: DocChronologyDoc): string {
  return `Document Name: ${chronDoc.doc.file.name}
Title: ${chronDoc.title ?? chronDoc.doc.title ?? ''}
Date: ${chronDoc.dateText ?? chronDoc.doc.dateText ?? ''}
Summary: ${chronDoc.summary ?? chronDoc.doc.summary ?? 'No summary available'}`;
}

/**
 * Converts an array of relativity field keys to their display names.
 * Falls back to the field key if no mapping or display name is found.
 */
export function getRelativityColumnDisplayNames(
  fieldKeys: string[],
  fieldMappings: FieldMappings | null,
): string[] {
  if (!fieldMappings) {
    return fieldKeys;
  }

  // Build a lookup map from fieldKey to displayName
  const fieldKeyToDisplayName = new Map<string, string>();
  for (const config of Object.values(fieldMappings)) {
    if (config.fieldKey && config.displayName) {
      fieldKeyToDisplayName.set(config.fieldKey, config.displayName);
    }
  }

  return fieldKeys.map((fieldKey) => fieldKeyToDisplayName.get(fieldKey) ?? fieldKey);
}
